import React, { useState, useMemo, useCallback } from "react";
import { toast, Toaster } from "sonner";

import AnalyzerHeader from "@/components/AnalyzerHeader";
import FloorPlanCanvas from "@/components/FloorPlanCanvas";
import Toolbar from "@/components/Toolbar";
import RotationDial from "@/components/RotationDial";
import SectorInspector from "@/components/SectorInspector";
import SectorTable from "@/components/SectorTable";
import MyPlansDialog from "@/components/MyPlansDialog";
import ExportPdfDialog from "@/components/ExportPdfDialog";
import BrandingDialog from "@/components/BrandingDialog";
import RoomPicker from "@/components/RoomPicker";
import RoomMarkerInspector from "@/components/RoomMarkerInspector";
import PdfPagePickerDialog from "@/components/PdfPagePickerDialog";
import { useAuth } from "@/context/AuthContext";
import { useFloorPlanUpload } from "@/hooks/useFloorPlanUpload";
import { useSessionPersistence } from "@/hooks/useSessionPersistence";
import { useCornerDetection } from "@/hooks/useCornerDetection";
import api from "@/lib/api";
import { loadFromURL } from "@/lib/fileLoader";
import { polygonCentroid, computeBBox, polygonArea, buildSectors } from "@/lib/sectors";
import { renderSnapshot } from "@/lib/snapshot";
import { buildVastuPDF, drawFooters } from "@/lib/pdfExport";
import { loadPurushaImage, drawPlanOverlayPage, drawRawImagePage, drawPlacementAnalysis } from "@/lib/placementReportPdf";
import { logError, logWarn } from "@/lib/log";

const TOAST_OPTIONS = { className: "font-body" };
const ALL_COUNTS = [8, 16, 32];
const remoteURL = (image) => (image?.src && image.src.startsWith("http") ? image.src : null);
const newMarkerId = () => `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export default function VastuAnalyzer() {
  const { user, logout } = useAuth();
  const [image, setImage] = useState(null);
  const [imageDims, setImageDims] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [polygon, setPolygon] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [mode, setMode] = useState("draw");
  const [roomType, setRoomType] = useState("kitchen");
  const [sectorCount, setSectorCount] = useState(16);
  const [showOverlay, setShowOverlay] = useState(true);
  const [showDegrees, setShowDegrees] = useState(true);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [hoveredSector, setHoveredSector] = useState(null);
  const [pinnedSector, setPinnedSector] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [dialog, setDialog] = useState(null); // 'plans' | 'pdf' | 'branding'

  const setLoadedImage = useCallback(({ image, width, height }) => {
    setImage(image);
    setImageDims({ width, height });
  }, []);

  const resetDrawing = useCallback(() => {
    setPolygon([]);
    setMarkers([]);
    setSelectedMarker(null);
    setPinnedSector(null);
  }, []);

  const onFileLoaded = useCallback((loaded) => { setLoadedImage(loaded); resetDrawing(); }, [setLoadedImage, resetDrawing]);
  const { upload, loadSample, pdfPick, clearPdfPick, pickPdfPage } = useFloorPlanUpload(onFileLoaded);

  const restoreSession = useCallback((s) => {
    if (s.loadedImage) { setLoadedImage(s.loadedImage); return; }
    if (s.polygon) setPolygon(s.polygon);
    if (s.rotation !== undefined) setRotation(s.rotation);
    if (s.sectorCount) setSectorCount(s.sectorCount);
    if (s.markers) setMarkers(s.markers);
  }, [setLoadedImage]);
  useSessionPersistence(user?.id, { polygon, rotation, sectorCount, markers, imageURL: remoteURL(image) }, restoreSession);
  const { corners } = useCornerDetection(image, imageDims);

  // Derived geometry
  const bbox = useMemo(() => computeBBox(polygon, imageDims?.width || 800, imageDims?.height || 600), [polygon, imageDims]);
  const center = useMemo(() => (polygon.length >= 1 ? polygonCentroid(polygon) : null), [polygon]);
  const hasPolygon = polygon.length >= 3;
  const sectors = useMemo(() => (center && hasPolygon ? buildSectors(sectorCount, center, polygon, bbox, rotation) : []),
    [center, hasPolygon, polygon, bbox, sectorCount, rotation]);
  const area = useMemo(() => polygonArea(polygon), [polygon]);
  const focusSector = pinnedSector !== null ? pinnedSector : hoveredSector;
  const activeSector = focusSector !== null ? sectors.find((s) => s.index === focusSector) : null;
  const activeMarker = markers.find((m) => m.id === selectedMarker) || null;

  // Polygon + marker editing
  const handleAddPoint = (p) => setPolygon((prev) => [...prev, p]);
  const handleMovePoint = (idx, p) => setPolygon((prev) => prev.map((pt, i) => (i === idx ? p : pt)));
  const handleUndo = () => setPolygon((prev) => prev.slice(0, -1));
  const handleClear = () => { setPolygon([]); setPinnedSector(null); toast("Polygon cleared"); };
  const handleAddMarker = (p) => {
    const id = newMarkerId();
    setMarkers((prev) => [...prev, { id, type: roomType, x: p.x, y: p.y }]);
    setSelectedMarker(id);
  };
  const handleMoveMarker = (id, p) => setMarkers((prev) => prev.map((m) => (m.id === id ? { ...m, x: p.x, y: p.y } : m)));
  const handleDeleteMarker = (id) => { setMarkers((prev) => prev.filter((m) => m.id !== id)); setSelectedMarker(null); };
  const handleSelectMarker = (id) => { setSelectedMarker((prev) => (prev === id ? null : id)); setPinnedSector(null); };
  const handleClickSector = (idx) => { setPinnedSector((prev) => (prev === idx ? null : idx)); setSelectedMarker(null); };

  // Exports — PDF only
  const requireSectors = () => { if (sectors.length === 0) { toast.error("Draw a polygon first"); return false; } return true; };
  // Build a labelled snapshot for a specific division count (all three maps go into the PDF)
  const snapshotFor = (count) => {
    const secs = buildSectors(count, center, polygon, bbox, rotation);
    return { sectorCount: count, canvas: renderSnapshot({ image, imageDims, rotation, polygon, sectors: secs, center, sectorCount: count, showOverlay, showDegrees, markers }) };
  };
  const handleExportPDF = async ({ pageFormat, planName }) => {
    try {
      let branding = null;
      try { branding = (await api.get("/profile/branding")).data; } catch (e) { logWarn("Branding unavailable, exporting unbranded PDF", e); }
      const maps = ALL_COUNTS.map((count) => snapshotFor(count));
      const doc = buildVastuPDF({ maps, planName, rotation, center, area, pageFormat, branding, markers, includeRoomReport: false, withFooters: false });

      // Append guidance pages into the same document: Purusha overlay, raw plan, room analysis
      const title = planName || "Untitled plan";
      const orient = maps[0].canvas.width > maps[0].canvas.height * 1.15 ? "landscape" : "portrait";
      const purushaImg = await loadPurushaImage();
      drawPlanOverlayPage(doc, { branding, title, plan: { image, imageDims, polygon, center, rotation, markers }, purushaImg, pageFormat, orient });
      drawRawImagePage(doc, { branding, title, image, imageDims, pageFormat, orient });
      drawPlacementAnalysis(doc, { branding, title, markers, center, rotation, pageFormat });
      drawFooters(doc, branding);

      const safe = (planName || "vastu-plan").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase();
      doc.save(`${safe}-vastu-report.pdf`);
      toast.success("PDF exported");
    } catch (e) {
      logError("PDF export failed", e);
      toast.error("Could not build PDF");
    }
  };

  // Saved plans
  const handleLoadPlan = async (plan) => {
    setPolygon(plan.polygon || []);
    setRotation(plan.rotation || 0);
    setSectorCount(plan.sector_count || 16);
    setMarkers(plan.markers || []);
    setSelectedMarker(null);
    setPinnedSector(null);
    if (!plan.image_url) return;
    try {
      setLoadedImage(await loadFromURL(plan.image_url));
    } catch (e) {
      logWarn("Plan image could not be loaded; geometry restored without it", e);
      toast("Plan loaded without its image (URL unavailable)");
    }
  };
  const currentPlanPayload = { polygon, rotation, sector_count: sectorCount, markers, image_url: remoteURL(image) };

  return (
    <div className="h-screen w-screen flex flex-col bg-slate-950 text-slate-100 font-body">
      <Toaster theme="dark" position="top-right" toastOptions={TOAST_OPTIONS} />

      <AnalyzerHeader
        user={user}
        sectorCount={sectorCount}
        onSectorCount={setSectorCount}
        onOpenPlans={() => setDialog("plans")}
        onOpenBranding={() => setDialog("branding")}
        onLogout={logout}
        exports={{ onPDF: () => requireSectors() && setDialog("pdf") }}
      />

      <div className="flex-1 flex overflow-hidden">
        <div className="p-3 border-r border-slate-800/60">
          <Toolbar
            mode={mode} setMode={setMode}
            onUpload={upload} onLoadSample={loadSample}
            onUndo={handleUndo} onClear={handleClear}
            hasImage={!!image} pointCount={polygon.length}
            showOverlay={showOverlay} setShowOverlay={setShowOverlay}
            showDegrees={showDegrees} setShowDegrees={setShowDegrees}
            snapEnabled={snapEnabled} setSnapEnabled={setSnapEnabled} cornerCount={corners.length}
          />
        </div>

        <div className="flex-1 flex flex-col p-4 gap-3 min-w-0">
          {mode === "marker" && <RoomPicker selected={roomType} onSelect={setRoomType} onClose={() => setMode("draw")} />}
          <FloorPlanCanvas
            image={image} imageDims={imageDims} rotation={rotation} polygon={polygon} mode={mode}
            sectorCount={sectorCount} showOverlay={showOverlay} showDegrees={showDegrees}
            onAddPoint={handleAddPoint} onMovePoint={handleMovePoint}
            hoveredSector={focusSector} onHoverSector={setHoveredSector} onClickSector={handleClickSector}
            markers={markers} selectedMarker={selectedMarker}
            onAddMarker={handleAddMarker} onMoveMarker={handleMoveMarker} onSelectMarker={handleSelectMarker}
            corners={corners} snapEnabled={snapEnabled}
          />
        </div>

        <aside className="w-80 xl:w-96 border-l border-slate-800/60 bg-slate-900/60 overflow-y-auto shrink-0 flex flex-col">
          <div className="p-4 border-b border-slate-800/60">
            <RotationDial value={rotation} onChange={setRotation} />
          </div>
          <div className="flex-1 min-h-0">
            {activeMarker
              ? <RoomMarkerInspector marker={activeMarker} center={hasPolygon ? center : null} rotation={rotation} onDelete={handleDeleteMarker} />
              : <SectorInspector sector={activeSector} count={sectorCount} rotation={rotation} />}
          </div>
          <div className="p-4 border-t border-slate-800/60">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Sector data</div>
            <SectorTable sectors={sectors} hoveredIndex={focusSector} onHover={setHoveredSector} onClick={handleClickSector} />
          </div>
        </aside>
      </div>

      <MyPlansDialog open={dialog === "plans"} onOpenChange={(o) => setDialog(o ? "plans" : null)} currentPlan={currentPlanPayload} onLoadPlan={handleLoadPlan} />
      <ExportPdfDialog open={dialog === "pdf"} onOpenChange={(o) => setDialog(o ? "pdf" : null)} onExport={handleExportPDF} />
      <BrandingDialog open={dialog === "branding"} onOpenChange={(o) => setDialog(o ? "branding" : null)} />
      <PdfPagePickerDialog pdf={pdfPick?.pdf} fileName={pdfPick?.fileName} open={!!pdfPick} onOpenChange={(o) => { if (!o) clearPdfPick(); }} onPick={pickPdfPage} />
    </div>
  );
}
